import React, { useMemo, useState } from "react";
import "./OnboardingQuiz.scss";
import { itx } from "../../../../translationItx/itx";
import {
  FINISH_TITLE_AFTER_BOOKING,
  QUIZ_DONE_ID,
  QUIZ_FLOW,
  QUIZ_START_ID,
  QuizOption,
  TranslatedText,
  getNodeDepth,
} from "./quizFlow";
import axios from "../../../../utils/axios";

function tt(text?: TranslatedText): string {
  return text ? itx(text.key, text.fallback) : "";
}

const WEEKDAY_LABELS = [
  itx("OnboardingQuiz.so", "So."),
  itx("OnboardingQuiz.mo", "Mo."),
  itx("OnboardingQuiz.di", "Di."),
  itx("OnboardingQuiz.mi", "Mi."),
  itx("OnboardingQuiz.do", "Do."),
  itx("OnboardingQuiz.fr", "Fr."),
  itx("OnboardingQuiz.sa", "Sa."),
];
const APPOINTMENT_HOURS = [10, 11, 12, 13, 14, 15];

function getNextWeekdays(count: number): Date[] {
  const days: Date[] = [];
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  cursor.setDate(cursor.getDate() + 1);

  while (days.length < count) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) {
      days.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function toLocalDateStr(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function formatDay(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${WEEKDAY_LABELS[date.getDay()]} ${day}.${month}.`;
}

interface OnboardingQuizProps {
  onComplete: () => void;
  email: string;
}

export default function OnboardingQuiz({ onComplete, email }: OnboardingQuizProps) {
  const [currentId, setCurrentId] = useState<string>(QUIZ_START_ID);
  const [history, setHistory] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [selectedHour, setSelectedHour] = useState<number | undefined>(undefined);
  const [bookedAppointment, setBookedAppointment] = useState(false);
  const [choices, setChoices] = useState<Record<string, string>>({});

  const node = QUIZ_FLOW[currentId];
  const weekdays = useMemo(() => getNextWeekdays(10), []);

  function advance(nextId: string, currentChoices: Record<string, string> = choices) {
    if (nextId === QUIZ_DONE_ID) {
      const infos = {
        email,
        choices: currentChoices,
      };
      axios
        .post("/api/onboarding/quiz-result", infos)
        .then((res) => {
          console.log("Quiz result submitted successfully", res);
        })
        .catch((err) => {
          console.error("Failed to submit quiz result", err);
        });
      onComplete();
      return;
    }
    setHistory((prev) => [...prev, currentId]);
    setSelectedDate(undefined);
    setSelectedHour(undefined);
    setCurrentId(nextId);
  }

  function goBack() {
    if (history.length === 0) return;
    const previousHistory = history.slice(0, -1);
    const previousId = history[history.length - 1];
    setHistory(previousHistory);
    setCurrentId(previousId);
  }

  function handleChoice(option: QuizOption) {
    const updatedChoices = { ...choices, [currentId]: option.value };
    setChoices(updatedChoices);
    advance(option.next, updatedChoices);
  }

  if (!node) {
    return null;
  }

  const totalSteps = history.length + getNodeDepth(currentId);
  const progressPercent = Math.min(100, Math.round(((history.length + 1) / totalSteps) * 100));

  return (
    <div className="onboarding-quiz">
      <div className="onboarding-quiz__progress-track">
        <div className="onboarding-quiz__progress-fill" style={{ width: `${progressPercent}%` }} />
      </div>

      <div className="onboarding-quiz__step" key={currentId}>
        {history.length > 0 && (
          <div className="onboarding-quiz__step-header">
            <button
              type="button"
              className="onboarding-quiz__back"
              onClick={goBack}
              aria-label={itx("onboardingQuiz.back", "Zurück")}
            >
              <i className="la la-arrow-left" />
            </button>
          </div>
        )}

        {currentId === "finish" && bookedAppointment && (
          <p className="onboarding-quiz__subtitle">{tt(FINISH_TITLE_AFTER_BOOKING)}</p>
        )}
        <h1 className="onboarding-quiz__title">{tt(node.title)}</h1>
        {"subtitle" in node && node.subtitle && <p className="onboarding-quiz__subtitle">{tt(node.subtitle)}</p>}
        {node.type === "info" && node.body && <p className="onboarding-quiz__subtitle">{tt(node.body)}</p>}

        {node.type === "choice" && (
          <div className="onboarding-quiz__options">
            {node.options.map((option) => (
              <button
                type="button"
                key={option.value}
                className="onboarding-quiz__option"
                onClick={() => handleChoice(option)}
              >
                {tt(option.label)}
              </button>
            ))}
          </div>
        )}

        {node.type === "scheduler" && (
          <div className="onboarding-quiz__scheduler">
            <div className="onboarding-quiz__scheduler-group">
              <span className="onboarding-quiz__scheduler-label">
                {itx("onboardingQuiz.scheduler.dayLabel", "Tag")}
              </span>
              <div className="onboarding-quiz__chips">
                {weekdays.map((date) => {
                  const isoDate = toLocalDateStr(date);
                  return (
                    <button
                      type="button"
                      key={isoDate}
                      className={`onboarding-quiz__chip${selectedDate === isoDate ? " -selected" : ""}`}
                      onClick={() => setSelectedDate(isoDate)}
                    >
                      {formatDay(date)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="onboarding-quiz__scheduler-group">
              <span className="onboarding-quiz__scheduler-label">
                {itx("onboardingQuiz.scheduler.timeLabel", "Uhrzeit")}
              </span>
              <div className="onboarding-quiz__chips">
                {APPOINTMENT_HOURS.map((hour) => (
                  <button
                    type="button"
                    key={hour}
                    className={`onboarding-quiz__chip${selectedHour === hour ? " -selected" : ""}`}
                    onClick={() => setSelectedHour(hour)}
                  >
                    {`${hour}:00`}
                  </button>
                ))}
              </div>
            </div>

            <div className="onboarding-quiz__actions">
              {selectedDate && selectedHour !== undefined && (
                <button
                  type="button"
                  className="onboarding-quiz__cta"
                  onClick={() => {
                    setBookedAppointment(true);
                    // send appointment data to backend here if needed
                    let text = "";
                    if (choices) {
                      for (const [key, value] of Object.entries(choices)) {
                        if (key == "role") {
                          text += `Rolle: ${value}\n`;
                        } else if (key.includes("schema")) {
                          text += `Gruppenadress-Schema: ${value}\n`;
                        } else if (key.includes("anzahl")) {
                          text += `Anzahl Projekte pro Jahr: ${value}\n`;
                        } else if (key.includes("demo")) {
                          text += `Demo Termin beansprucht: ${value}\n`;
                        } else if (key.includes("projekt")) {
                          text += `knx projekt: ${value}\n`;
                        } else if (key.includes("kenntnisse")) {
                          text += `Kenntnisse: ${value}\n`;
                        } else if (key.includes("unterstuetzung")) {
                          text += `Unterstützung gewünscht: ${value}\n`;
                        } else if (key.includes("zeit")) {
                          text += `Zeitplan: ${value}\n`;
                        } else if (key.includes("stehtfest")) {
                          text += `KNX festgelegt: ${value}\n`;
                        } else {
                          text += `${key}: ${value}\n`;
                        }
                      }
                    } else {
                      text = "no choices"; // no itx
                    }

                    let subject = "Terminbuchung";
                    if (state.loggedInUser) {
                      subject = subject + ` von ${state.loggedInUser.name}`;
                    }
                    subject = subject + ` (${email || state.loggedInUser?.email || ""})`;
                    subject =
                      subject +
                      ` am ${formatDay(
                        new Date(
                          selectedDate + "T12:00:00", // no itx
                        ),
                      )} um ${selectedHour}:00 Uhr`;
                    axios
                      .post("/api/support/appointment-booked", {
                        from: { email: email || state.loggedInUser?.email || "", name: state.loggedInUser?.name || "" },
                        subject: subject,
                        text: text,
                        day: selectedDate,
                        time: `${selectedHour}:00`,
                      })
                      .then(() => {
                        console.log("Appointment booked successfully");
                      })
                      .catch(() => {
                        console.error("Failed to book appointment");
                      });
                    advance(node.next);
                  }}
                >
                  {itx("onboardingQuiz.scheduler.cta", "Buchen und weiter")}
                </button>
              )}
              <button
                type="button"
                className="onboarding-quiz__skip"
                onClick={() => {
                  setBookedAppointment(false);
                  advance(node.next);
                }}
              >
                {itx("onboardingQuiz.scheduler.skip", "Überspringen")}
              </button>
            </div>
          </div>
        )}

        {node.type === "info" && (
          <div className="onboarding-quiz__actions">
            <button type="button" className="onboarding-quiz__cta" onClick={() => advance(node.next)}>
              {tt(node.ctaLabel)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
